import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { APIListWrapper } from '../models/common/api-list-wrapper.model';
import { APIResponseWrapper } from '../models/common/api-response-wrapper';
import { TransportPlanDirectoryPage, TransportPlanDirectoryQuery } from './plan-directory/transport-plan-directory.model';
import { BulkRunDirectoryPage, BulkRunDirectoryQuery } from './bulk-planning/bulk-run-directory.model';
import {
  ApplyTransportPlanResponse,
  AssignReleaseLocationRequest,
  BulkPlanningApproveResponse,
  BulkPlanningAdjustmentAudit,
  BulkPlanningProfile,
  BulkPlanningProfileMatchResponse,
  BulkPlanningResourceOptions,
  BulkPlanningRun,
  BulkPlanningValidationResult,
  BulkOrderReleaseImportCreateRequest,
  BulkOrderReleaseImportPreview,
  BulkOrderReleaseImportResult,
  CreateBulkPlanningRunRequest,
  CreateMatchedBulkPlanningRunsResponse,
  BulkPlanningSelectionSpec,
  CrossDockOrchestration,
  CreateTransportPlanRequest,
  ManualTransportPlanRequest,
  PosmManualOverrideRequest,
  PosmRouteTemplate,
  PosmThirdPartyBookingRequest,
  PosmThirdPartyTruckBooking,
  PosmTransportApprovalRequest,
  PosmTruckAssignmentOverride,
  PosmTruckAssignmentRule,
  PosmTruckPlanningSummary,
  PosmTaskImportRequest,
  PublicOrderReleaseTracking,
  TruckPlanBuildPreview,
  TruckPlanBuildPreviewRequest,
  TruckPlanCreateFromSelectionRequest,
  TruckPlanCreateFromSelectionResponse,
  TransportOptimizationPlan,
  LocationZone,
  TransportPlanRoutePricingRequest,
  TransportOrderRelease,
  ReleaseCancellationImpact,
  TransportOrderReleaseGroupPage,
  TransportOrderReleaseFilterFacets,
  TransportOrderReleaseSearchParams,
  ShipmentMatchPage,
  ShipmentReleaseAttachmentPreview,
  ShipmentReleaseAttachmentApplyRequest,
  ShipmentDeliveryReleaseAmendment,
  ShipmentDeliveryReleaseAmendmentApplyRequest
} from './transport-planning.model';

@Injectable({ providedIn: 'root' })
export class TransportPlanningService {
  private readonly apiUrl = `${environment.restApiUrl}transport-planning`;
  private readonly publicTrackingUrl = `${environment.restApiUrl}public/transport/order-release-tracking`;

  constructor(private http: HttpClient) {}

  getReleases(filters: TransportOrderReleaseSearchParams): Observable<APIListWrapper<TransportOrderRelease>> {
    return this.http.get<APIListWrapper<TransportOrderRelease>>(`${this.apiUrl}/order-releases`, {
      params: this.releaseQueryParams(filters, true)
    });
  }

  getReleaseGroups(filters: TransportOrderReleaseSearchParams): Observable<TransportOrderReleaseGroupPage> {
    return this.http.get<TransportOrderReleaseGroupPage>(`${this.apiUrl}/order-release-groups`, {
      params: this.releaseQueryParams(filters, true)
    });
  }

  getReleaseFilterFacets(filters: TransportOrderReleaseSearchParams): Observable<TransportOrderReleaseFilterFacets> {
    return this.http.get<TransportOrderReleaseFilterFacets>(`${this.apiUrl}/order-releases/filter-facets`, {
      params: this.releaseQueryParams(filters, false)
    });
  }

  private releaseQueryParams(filters: TransportOrderReleaseSearchParams, includePaging: boolean): HttpParams {
    let params = new HttpParams().set('owningCompanyId', filters.owningCompanyId.toString());
    if (includePaging) {
      params = params.set('page', String(filters.page ?? 0)).set('pageSize', String(filters.pageSize ?? 12));
    }

    params = this.setParam(params, 'release', filters.release);
    params = this.setParam(params, 'releaseRole', filters.releaseRole);
    params = this.setParam(params, 'sourceType', filters.sourceType);
    params = this.setParam(params, 'sourceNumber', filters.sourceNumber);
    params = this.setParam(params, 'logisticsProjectId', filters.logisticsProjectId);
    params = this.setMultiParam(params, 'logisticsProjectId', filters.logisticsProjectIds);
    params = this.setParam(params, 'countryId', filters.countryId);
    params = this.setParam(params, 'cityId', filters.cityId);
    params = this.setParam(params, 'pickupCountryId', filters.pickupCountryId);
    params = this.setParam(params, 'pickupCityId', filters.pickupCityId);
    params = this.setParam(params, 'dropoffCountryId', filters.dropoffCountryId);
    params = this.setParam(params, 'dropoffCityId', filters.dropoffCityId);
    params = this.setParam(params, 'pickupZoneId', filters.pickupZoneId);
    params = this.setMultiParam(params, 'pickupCountryId', filters.pickupCountryIds);
    params = this.setMultiParam(params, 'pickupCityId', filters.pickupCityIds);
    params = this.setMultiParam(params, 'dropoffCountryId', filters.dropoffCountryIds);
    params = this.setMultiParam(params, 'dropoffCityId', filters.dropoffCityIds);
    params = this.setMultiParam(params, 'pickupZoneId', filters.pickupZoneIds);
    params = this.setParam(params, 'includePickupNeighbours', filters.includePickupNeighbours);
    params = this.setParam(params, 'dropoffZoneId', filters.dropoffZoneId);
    params = this.setMultiParam(params, 'dropoffZoneId', filters.dropoffZoneIds);
    params = this.setParam(params, 'includeDropoffNeighbours', filters.includeDropoffNeighbours);
    params = this.setParam(params, 'originLocationId', filters.originLocationId);
    params = this.setParam(params, 'destinationLocationId', filters.destinationLocationId);
    params = this.setParam(params, 'crmClientId', filters.crmClientId);
    params = this.setMultiParam(params, 'originLocationId', filters.originLocationIds);
    params = this.setMultiParam(params, 'destinationLocationId', filters.destinationLocationIds);
    params = this.setMultiParam(params, 'crmClientId', filters.crmClientIds);
    params = this.setParam(params, 'customer', filters.customer);
    params = this.setParam(params, 'origin', filters.origin);
    params = this.setParam(params, 'destination', filters.destination);
    params = this.setParam(params, 'needFrom', filters.needFrom);
    params = this.setParam(params, 'needTo', filters.needTo);
    params = this.setParam(params, 'status', filters.status);
    params = this.setParam(params, 'validation', filters.validation);
    params = this.setParam(params, 'load', filters.load);
    params = this.setParam(params, 'itemCode', filters.itemCode);
    params = this.setParam(params, 'itemName', filters.itemName);
    params = this.setParam(params, 'itemSize', filters.itemSize);
    params = this.setParam(params, 'actionRequired', filters.actionRequired);
    params = this.setParam(params, 'sourceLineType', filters.sourceLineType);
    params = this.setParam(params, 'sourceLineId', filters.sourceLineId);
    params = this.setParam(params, 'quantityMin', filters.quantityMin);
    params = this.setParam(params, 'quantityMax', filters.quantityMax);
    params = this.setParam(params, 'uom', filters.uom);
    params = this.setParam(params, 'regionId', filters.regionId);
    params = this.setParam(params, 'warehouseId', filters.warehouseId);
    params = this.setParam(params, 'inventoryItemId', filters.inventoryItemId);
    params = this.setMultiParam(params, 'uom', filters.uoms);
    params = this.setMultiParam(params, 'regionId', filters.regionIds);
    params = this.setMultiParam(params, 'warehouseId', filters.warehouseIds);
    params = this.setMultiParam(params, 'inventoryItemId', filters.inventoryItemIds);

    return params;
  }

  getLocationZones(owningCompanyId: number, activeOnly = false): Observable<LocationZone[]> {
    const params = new HttpParams()
      .set('owningCompanyId', owningCompanyId)
      .set('activeOnly', activeOnly);
    return this.http.get<LocationZone[]>(`${this.apiUrl}/location-zones`, { params });
  }

  getLocationZone(zoneId: number, owningCompanyId: number): Observable<LocationZone> {
    return this.http.get<LocationZone>(`${this.apiUrl}/location-zones/${zoneId}`, {
      params: new HttpParams().set('owningCompanyId', owningCompanyId)
    });
  }

  createLocationZone(zone: LocationZone): Observable<LocationZone> {
    return this.http.post<LocationZone>(`${this.apiUrl}/location-zones`, zone);
  }

  updateLocationZone(zone: LocationZone): Observable<LocationZone> {
    return this.http.put<LocationZone>(`${this.apiUrl}/location-zones/${zone.id}`, zone);
  }

  setLocationZoneActive(zoneId: number, owningCompanyId: number, active: boolean): Observable<LocationZone> {
    const params = new HttpParams()
      .set('owningCompanyId', owningCompanyId)
      .set('active', active);
    return this.http.patch<LocationZone>(`${this.apiUrl}/location-zones/${zoneId}/active`, {}, { params });
  }

  getRelease(releaseId: number): Observable<TransportOrderRelease> {
    return this.http.get<TransportOrderRelease>(`${this.apiUrl}/order-releases/${releaseId}`);
  }

  getReleaseCancellationImpact(releaseId: number): Observable<ReleaseCancellationImpact> {
    return this.http.get<ReleaseCancellationImpact>(`${this.apiUrl}/order-releases/${releaseId}/cancel-impact`);
  }

  cancelRelease(releaseId: number, reason: string): Observable<ReleaseCancellationImpact> {
    return this.http.post<ReleaseCancellationImpact>(`${this.apiUrl}/order-releases/${releaseId}/cancel`, { reason });
  }

  prepareRescheduledRelease(releaseId: number, needBy: Date): Observable<TransportOrderRelease> {
    return this.http.post<TransportOrderRelease>(`${this.apiUrl}/order-releases/${releaseId}/prepare-reschedule`, { needBy });
  }

  getCrossDockOrchestrations(owningCompanyId: number, status?: string | null): Observable<CrossDockOrchestration[]> {
    let params = new HttpParams().set('owningCompanyId', owningCompanyId);
    if (status) params = params.set('status', status);
    return this.http.get<CrossDockOrchestration[]>(`${this.apiUrl}/orchestrations`, { params });
  }

  getCrossDockOrchestration(crossDockOrchestrationId: number): Observable<CrossDockOrchestration> {
    return this.http.get<CrossDockOrchestration>(`${this.apiUrl}/orchestrations/${crossDockOrchestrationId}`);
  }

  getReleaseCrossDockOrchestration(releaseId: number): Observable<CrossDockOrchestration> {
    return this.http.get<CrossDockOrchestration>(`${this.apiUrl}/order-releases/${releaseId}/orchestration`);
  }

  getPosmProjectReleases(projectId: number, owningCompanyId: number): Observable<TransportOrderRelease[]> {
    return this.http.get<TransportOrderRelease[]>(`${this.apiUrl}/order-releases/posm-project/${projectId}`, {
      params: new HttpParams().set('owningCompanyId', owningCompanyId)
    });
  }

  getPosmRouteTemplates(owningCompanyId: number): Observable<PosmRouteTemplate[]> {
    return this.http.get<PosmRouteTemplate[]>(`${this.apiUrl}/posm/route-templates`, {
      params: new HttpParams().set('owningCompanyId', owningCompanyId)
    });
  }

  savePosmRouteTemplate(template: PosmRouteTemplate): Observable<PosmRouteTemplate> {
    return this.http.post<PosmRouteTemplate>(`${this.apiUrl}/posm/route-templates`, template);
  }

  getPosmAssignmentRules(owningCompanyId: number): Observable<PosmTruckAssignmentRule[]> {
    return this.http.get<PosmTruckAssignmentRule[]>(`${this.apiUrl}/posm/assignment-rules`, {
      params: new HttpParams().set('owningCompanyId', owningCompanyId)
    });
  }

  savePosmAssignmentRule(rule: PosmTruckAssignmentRule): Observable<PosmTruckAssignmentRule> {
    return this.http.post<PosmTruckAssignmentRule>(`${this.apiUrl}/posm/assignment-rules`, rule);
  }

  getPosmPlanningSummary(projectId: number, owningCompanyId: number, horizonStart?: Date | string | null, horizonEnd?: Date | string | null): Observable<PosmTruckPlanningSummary> {
    let params = new HttpParams().set('owningCompanyId', owningCompanyId);
    if (horizonStart) params = params.set('horizonStart', new Date(horizonStart).toISOString());
    if (horizonEnd) params = params.set('horizonEnd', new Date(horizonEnd).toISOString());
    return this.http.get<PosmTruckPlanningSummary>(`${this.apiUrl}/posm/projects/${projectId}/planning-summary`, { params });
  }

  getPosmAssignmentOverrides(projectId: number, owningCompanyId: number): Observable<PosmTruckAssignmentOverride[]> {
    return this.http.get<PosmTruckAssignmentOverride[]>(`${this.apiUrl}/posm/projects/${projectId}/assignment-overrides`, {
      params: new HttpParams().set('owningCompanyId', owningCompanyId)
    });
  }

  createPosmAssignmentOverride(request: PosmManualOverrideRequest): Observable<PosmTruckAssignmentOverride> {
    return this.http.post<PosmTruckAssignmentOverride>(`${this.apiUrl}/posm/assignment-overrides`, request);
  }

  approvePosmAssignmentOverride(overrideId: number, owningCompanyId: number, request: PosmTransportApprovalRequest): Observable<PosmTruckAssignmentOverride> {
    return this.http.post<PosmTruckAssignmentOverride>(`${this.apiUrl}/posm/assignment-overrides/${overrideId}/approve`, request, {
      params: new HttpParams().set('owningCompanyId', owningCompanyId)
    });
  }

  rejectPosmAssignmentOverride(overrideId: number, owningCompanyId: number, request: PosmTransportApprovalRequest): Observable<PosmTruckAssignmentOverride> {
    return this.http.post<PosmTruckAssignmentOverride>(`${this.apiUrl}/posm/assignment-overrides/${overrideId}/reject`, request, {
      params: new HttpParams().set('owningCompanyId', owningCompanyId)
    });
  }

  getPosmThirdPartyBookings(projectId: number, owningCompanyId: number): Observable<PosmThirdPartyTruckBooking[]> {
    return this.http.get<PosmThirdPartyTruckBooking[]>(`${this.apiUrl}/posm/projects/${projectId}/third-party-bookings`, {
      params: new HttpParams().set('owningCompanyId', owningCompanyId)
    });
  }

  createPosmThirdPartyBooking(request: PosmThirdPartyBookingRequest): Observable<PosmThirdPartyTruckBooking> {
    return this.http.post<PosmThirdPartyTruckBooking>(`${this.apiUrl}/posm/third-party-bookings`, request);
  }

  approvePosmThirdPartyBooking(bookingId: number, owningCompanyId: number, request: PosmTransportApprovalRequest): Observable<PosmThirdPartyTruckBooking> {
    return this.http.post<PosmThirdPartyTruckBooking>(`${this.apiUrl}/posm/third-party-bookings/${bookingId}/approve`, request, {
      params: new HttpParams().set('owningCompanyId', owningCompanyId)
    });
  }

  rejectPosmThirdPartyBooking(bookingId: number, owningCompanyId: number, request: PosmTransportApprovalRequest): Observable<PosmThirdPartyTruckBooking> {
    return this.http.post<PosmThirdPartyTruckBooking>(`${this.apiUrl}/posm/third-party-bookings/${bookingId}/reject`, request, {
      params: new HttpParams().set('owningCompanyId', owningCompanyId)
    });
  }

  createRelease(release: TransportOrderRelease): Observable<TransportOrderRelease> {
    return this.http.post<TransportOrderRelease>(`${this.apiUrl}/order-releases`, release);
  }

  downloadBulkOrderReleaseTemplate(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/order-releases/bulk-import/template`, { responseType: 'blob' });
  }

  previewBulkOrderReleaseExcel(file: File, owningCompanyId: number): Observable<BulkOrderReleaseImportPreview> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<BulkOrderReleaseImportPreview>(`${this.apiUrl}/order-releases/bulk-import/preview`, formData, {
      params: new HttpParams().set('owningCompanyId', owningCompanyId)
    });
  }

  extractBulkOrderReleaseDocument(file: File, owningCompanyId: number): Observable<BulkOrderReleaseImportPreview> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<BulkOrderReleaseImportPreview>(`${this.apiUrl}/order-releases/bulk-import/ai-extract`, formData, {
      params: new HttpParams().set('owningCompanyId', owningCompanyId)
    });
  }

  createBulkOrderReleases(request: BulkOrderReleaseImportCreateRequest): Observable<BulkOrderReleaseImportResult> {
    return this.http.post<BulkOrderReleaseImportResult>(`${this.apiUrl}/order-releases/bulk-import/create`, request);
  }

  assignReleaseLocation(releaseId: number, request: AssignReleaseLocationRequest): Observable<TransportOrderRelease> {
    return this.http.post<TransportOrderRelease>(`${this.apiUrl}/order-releases/${releaseId}/assign-location`, request);
  }

  getPublicOrderReleaseTracking(trackingNumber: string): Observable<PublicOrderReleaseTracking> {
    return this.http.get<APIResponseWrapper<PublicOrderReleaseTracking>>(this.publicTrackingUrl, {
      params: new HttpParams().set('trackingNumber', trackingNumber)
    }).pipe(map(response => response.data));
  }

  importSalesOrders(request: any): Observable<TransportOrderRelease[]> {
    return this.http.post<TransportOrderRelease[]>(`${this.apiUrl}/order-releases/import/sales-orders`, request);
  }

  importPosm(request: any): Observable<TransportOrderRelease[]> {
    return this.http.post<TransportOrderRelease[]>(`${this.apiUrl}/order-releases/import/posm`, request);
  }

  importPosmTasks(request: PosmTaskImportRequest): Observable<TransportOrderRelease[]> {
    return this.http.post<TransportOrderRelease[]>(`${this.apiUrl}/order-releases/import/posm-tasks`, request);
  }

  getPlans(owningCompanyId: number): Observable<TransportOptimizationPlan[]> {
    return this.http.get<TransportOptimizationPlan[]>(`${this.apiUrl}/plans`, {
      params: new HttpParams().set('owningCompanyId', owningCompanyId)
    });
  }

  searchPlanDirectory(query: TransportPlanDirectoryQuery): Observable<TransportPlanDirectoryPage> {
    let params = new HttpParams()
      .set('owningCompanyId', String(query.owningCompanyId))
      .set('page', String(query.page))
      .set('size', String(query.size))
      .set('sortField', query.sortField)
      .set('sortOrder', String(query.sortOrder));
    for (const key of ['search', 'planNumber', 'release', 'project', 'source', 'driver', 'vehicle',
      'origin', 'destination', 'assignment', 'horizonFrom', 'horizonTo', 'createdFrom', 'createdTo'] as const) {
      const value = query[key];
      if (value) params = params.set(key, value);
    }
    for (const status of query.statuses ?? []) params = params.append('statuses', status);
    return this.http.get<TransportPlanDirectoryPage>(`${this.apiUrl}/plans/directory`, { params });
  }

  getPosmProjectPlans(projectId: number, owningCompanyId: number): Observable<TransportOptimizationPlan[]> {
    return this.http.get<TransportOptimizationPlan[]>(`${this.apiUrl}/plans/posm-project/${projectId}`, {
      params: new HttpParams().set('owningCompanyId', owningCompanyId)
    });
  }

  createPlan(request: CreateTransportPlanRequest): Observable<TransportOptimizationPlan> {
    return this.http.post<TransportOptimizationPlan>(`${this.apiUrl}/plans`, request);
  }

  buildPlanPreview(request: TruckPlanBuildPreviewRequest): Observable<TruckPlanBuildPreview> {
    return this.http.post<TruckPlanBuildPreview>(`${this.apiUrl}/plans/build-preview`, request);
  }

  createPlanFromSelection(request: TruckPlanCreateFromSelectionRequest): Observable<TruckPlanCreateFromSelectionResponse> {
    return this.http.post<TruckPlanCreateFromSelectionResponse>(`${this.apiUrl}/plans/create-from-selection`, request);
  }

  getPlan(planId: number): Observable<TransportOptimizationPlan> {
    return this.http.get<TransportOptimizationPlan>(`${this.apiUrl}/plans/${planId}`);
  }

  optimizePlan(planId: number): Observable<TransportOptimizationPlan> {
    return this.http.post<TransportOptimizationPlan>(`${this.apiUrl}/plans/${planId}/optimize`, {});
  }

  saveManualRoutes(planId: number, request: ManualTransportPlanRequest): Observable<TransportOptimizationPlan> {
    return this.http.post<TransportOptimizationPlan>(`${this.apiUrl}/plans/${planId}/manual-routes`, request);
  }

  saveRoutePricing(planId: number, request: TransportPlanRoutePricingRequest): Observable<TransportOptimizationPlan> {
    return this.http.post<TransportOptimizationPlan>(`${this.apiUrl}/plans/${planId}/route-pricing`, request);
  }

  applyPlan(planId: number): Observable<ApplyTransportPlanResponse> {
    return this.http.post<ApplyTransportPlanResponse>(`${this.apiUrl}/plans/${planId}/apply`, {});
  }

  cancelPlan(planId: number, reason: string): Observable<TransportOptimizationPlan> {
    return this.http.post<TransportOptimizationPlan>(`${this.apiUrl}/plans/${planId}/cancel`, { reason });
  }

  getBulkPlanningProfiles(owningCompanyId: number): Observable<BulkPlanningProfile[]> {
    return this.http.get<BulkPlanningProfile[]>(`${this.apiUrl}/planning-profiles`, {
      params: new HttpParams().set('owningCompanyId', owningCompanyId)
    });
  }

  createBulkPlanningProfile(profile: BulkPlanningProfile): Observable<BulkPlanningProfile> {
    return this.http.post<BulkPlanningProfile>(`${this.apiUrl}/planning-profiles`, profile);
  }

  updateBulkPlanningProfile(profile: BulkPlanningProfile): Observable<BulkPlanningProfile> {
    return this.http.put<BulkPlanningProfile>(
      `${this.apiUrl}/planning-profiles/${profile.profileId}`,
      profile
    );
  }

  matchBulkPlanningProfiles(request: {
    owningCompanyId: number;
    horizonStart: string | Date;
    horizonEnd: string | Date;
    selection: BulkPlanningSelectionSpec;
  }): Observable<BulkPlanningProfileMatchResponse> {
    return this.http.post<BulkPlanningProfileMatchResponse>(
      `${this.apiUrl}/planning-profiles/match-releases`, request
    );
  }

  getBulkPlanningRuns(owningCompanyId: number): Observable<BulkPlanningRun[]> {
    return this.http.get<BulkPlanningRun[]>(`${this.apiUrl}/bulk-plan-runs`, {
      params: new HttpParams().set('owningCompanyId', owningCompanyId)
    });
  }

  searchBulkRunDirectory(query: BulkRunDirectoryQuery): Observable<BulkRunDirectoryPage> {
    let params = new HttpParams()
      .set('owningCompanyId', String(query.owningCompanyId))
      .set('page', String(query.page))
      .set('size', String(query.size))
      .set('sortField', query.sortField)
      .set('sortOrder', String(query.sortOrder));
    for (const key of ['search', 'runNumber', 'profile', 'triggerType', 'release', 'project',
      'driver', 'vehicle', 'origin', 'destination', 'horizonFrom', 'horizonTo',
      'createdFrom', 'createdTo'] as const) {
      const value = query[key];
      if (value) params = params.set(key, value);
    }
    for (const status of query.statuses ?? []) params = params.append('statuses', status);
    return this.http.get<BulkRunDirectoryPage>(`${this.apiUrl}/bulk-plan-runs/directory`, { params });
  }

  getBulkPlanningRun(runId: number, owningCompanyId: number): Observable<BulkPlanningRun> {
    return this.http.get<BulkPlanningRun>(`${this.apiUrl}/bulk-plan-runs/${runId}`, {
      params: new HttpParams().set('owningCompanyId', owningCompanyId)
    });
  }

  getBulkPlanningScenarioPlan(
    runId: number,
    scenarioId: number,
    owningCompanyId: number
  ): Observable<TransportOptimizationPlan> {
    return this.http.get<TransportOptimizationPlan>(
      `${this.apiUrl}/bulk-plan-runs/${runId}/scenarios/${scenarioId}/plan`,
      { params: new HttpParams().set('owningCompanyId', owningCompanyId) }
    );
  }

  createBulkPlanningRun(request: CreateBulkPlanningRunRequest): Observable<BulkPlanningRun> {
    return this.http.post<BulkPlanningRun>(`${this.apiUrl}/bulk-plan-runs`, request);
  }

  createMatchedBulkPlanningRuns(request: {
    owningCompanyId: number;
    runName?: string;
    triggerType?: 'INTERACTIVE' | 'SCHEDULED';
    horizonStart: string | Date;
    horizonEnd: string | Date;
    selection: BulkPlanningSelectionSpec;
  }): Observable<CreateMatchedBulkPlanningRunsResponse> {
    return this.http.post<CreateMatchedBulkPlanningRunsResponse>(
      `${this.apiUrl}/bulk-plan-runs/matched`, request
    );
  }

  createBulkPlanningScenario(
    runId: number,
    owningCompanyId: number,
    request: { scenarioCode?: string; scenarioName?: string; objective: string }
  ): Observable<BulkPlanningRun> {
    return this.http.post<BulkPlanningRun>(`${this.apiUrl}/bulk-plan-runs/${runId}/scenarios`, request, {
      params: new HttpParams().set('owningCompanyId', owningCompanyId)
    });
  }

  createBulkPlanningPlannerCopy(
    runId: number,
    scenarioId: number,
    owningCompanyId: number,
    request: { scenarioName?: string; reason: string; summary?: string; actorCustomerId?: number; expectedVersion?: number }
  ): Observable<TransportOptimizationPlan> {
    return this.http.post<TransportOptimizationPlan>(
      `${this.apiUrl}/bulk-plan-runs/${runId}/scenarios/${scenarioId}/planner-copy`, request,
      { params: new HttpParams().set('owningCompanyId', owningCompanyId) }
    );
  }

  getBulkPlanningResourceOptions(runId: number, scenarioId: number, routeId: number, owningCompanyId: number) {
    return this.http.get<BulkPlanningResourceOptions>(
      `${this.apiUrl}/bulk-plan-runs/${runId}/scenarios/${scenarioId}/routes/${routeId}/resource-options`,
      { params: new HttpParams().set('owningCompanyId', owningCompanyId) }
    );
  }

  changeBulkPlanningRouteResources(runId: number, scenarioId: number, routeId: number,
    owningCompanyId: number, request: {
      driverSnapshotId?: number; vehicleSnapshotId?: number; expectedRouteVersion?: number;
      reason: string; actorCustomerId?: number;
    }): Observable<TransportOptimizationPlan> {
    return this.http.put<TransportOptimizationPlan>(
      `${this.apiUrl}/bulk-plan-runs/${runId}/scenarios/${scenarioId}/routes/${routeId}/resources`, request,
      { params: new HttpParams().set('owningCompanyId', owningCompanyId) }
    );
  }

  moveBulkPlanningRelease(runId: number, scenarioId: number, releaseId: number, owningCompanyId: number,
    request: { sourceRouteId: number; targetRouteId?: number; expectedSourceRouteVersion?: number;
      expectedTargetRouteVersion?: number; reason: string; actorCustomerId?: number }): Observable<TransportOptimizationPlan> {
    return this.http.put<TransportOptimizationPlan>(
      `${this.apiUrl}/bulk-plan-runs/${runId}/scenarios/${scenarioId}/releases/${releaseId}/move`, request,
      { params: new HttpParams().set('owningCompanyId', owningCompanyId) }
    );
  }

  resequenceBulkPlanningStops(runId: number, scenarioId: number, routeId: number, owningCompanyId: number,
    request: { stops: Array<{ releaseId?: number; stopType?: string }>; expectedRouteVersion?: number;
      reason: string; actorCustomerId?: number }): Observable<TransportOptimizationPlan> {
    return this.http.put<TransportOptimizationPlan>(
      `${this.apiUrl}/bulk-plan-runs/${runId}/scenarios/${scenarioId}/routes/${routeId}/stop-sequence`, request,
      { params: new HttpParams().set('owningCompanyId', owningCompanyId) }
    );
  }

  saveBulkPlanningRouteFinancials(runId: number, scenarioId: number, routeId: number, owningCompanyId: number,
    request: { buyingPrice?: number | null; sellingPrice?: number | null; allocationBasis: string;
      projectAllocations?: Array<{ logisticsProjectId: number; allocationPercentage: number }>;
      expectedRouteVersion?: number; reason: string; actorCustomerId?: number }): Observable<TransportOptimizationPlan> {
    return this.http.put<TransportOptimizationPlan>(
      `${this.apiUrl}/bulk-plan-runs/${runId}/scenarios/${scenarioId}/routes/${routeId}/financials`, request,
      { params: new HttpParams().set('owningCompanyId', owningCompanyId) }
    );
  }

  validateBulkPlanningScenario(runId: number, scenarioId: number, owningCompanyId: number,
    request: { expectedVersion?: number; reason?: string; actorCustomerId?: number }) {
    return this.http.post<BulkPlanningValidationResult>(
      `${this.apiUrl}/bulk-plan-runs/${runId}/scenarios/${scenarioId}/validate`, request,
      { params: new HttpParams().set('owningCompanyId', owningCompanyId) }
    );
  }

  getBulkPlanningAdjustmentAudit(runId: number, scenarioId: number, owningCompanyId: number) {
    return this.http.get<BulkPlanningAdjustmentAudit[]>(
      `${this.apiUrl}/bulk-plan-runs/${runId}/scenarios/${scenarioId}/adjustment-audit`,
      { params: new HttpParams().set('owningCompanyId', owningCompanyId) }
    );
  }

  setBulkPlanningReleaseExcluded(
    runId: number,
    owningCompanyId: number,
    releaseId: number,
    excluded: boolean,
    reason?: string,
    scenarioId?: number,
    expectedScenarioVersion?: number
  ): Observable<BulkPlanningRun> {
    return this.http.post<BulkPlanningRun>(`${this.apiUrl}/bulk-plan-runs/${runId}/releases/exclusion`, {
      releaseId,
      excluded,
      reason,
      scenarioId,
      expectedScenarioVersion
    }, {
      params: new HttpParams().set('owningCompanyId', owningCompanyId)
    });
  }

  setBulkPlanningRouteLocked(
    runId: number,
    scenarioId: number,
    routeId: number,
    owningCompanyId: number,
    locked: boolean,
    expectedVersion?: number
  ): Observable<BulkPlanningRun> {
    return this.http.put<BulkPlanningRun>(
      `${this.apiUrl}/bulk-plan-runs/${runId}/scenarios/${scenarioId}/routes/${routeId}/lock`,
      { locked, expectedVersion },
      { params: new HttpParams().set('owningCompanyId', owningCompanyId) }
    );
  }

  reoptimizeBulkPlanningScenario(
    runId: number,
    scenarioId: number,
    owningCompanyId: number
  ): Observable<BulkPlanningRun> {
    return this.http.post<BulkPlanningRun>(
      `${this.apiUrl}/bulk-plan-runs/${runId}/scenarios/${scenarioId}/reoptimize`,
      {},
      { params: new HttpParams().set('owningCompanyId', owningCompanyId) }
    );
  }

  retryBulkPlanningRun(runId: number, owningCompanyId: number): Observable<BulkPlanningRun> {
    return this.http.post<BulkPlanningRun>(`${this.apiUrl}/bulk-plan-runs/${runId}/retry`, {}, {
      params: new HttpParams().set('owningCompanyId', owningCompanyId)
    });
  }

  approveAndApplyBulkPlanningScenario(
    runId: number,
    scenarioId: number,
    owningCompanyId: number
  ): Observable<BulkPlanningApproveResponse> {
    return this.http.post<BulkPlanningApproveResponse>(
      `${this.apiUrl}/bulk-plan-runs/${runId}/scenarios/${scenarioId}/approve-and-apply`,
      {},
      { params: new HttpParams().set('owningCompanyId', owningCompanyId) }
    );
  }

  cancelBulkPlanningRun(runId: number, owningCompanyId: number): Observable<BulkPlanningRun> {
    return this.http.post<BulkPlanningRun>(`${this.apiUrl}/bulk-plan-runs/${runId}/cancel`, {}, {
      params: new HttpParams().set('owningCompanyId', owningCompanyId)
    });
  }

  getReleaseMatches(
    releaseId: number,
    owningCompanyId: number,
    page = 0,
    size = 10
  ): Observable<ShipmentMatchPage> {
    const params = new HttpParams()
      .set('owningCompanyId', owningCompanyId)
      .set('page', page)
      .set('size', size);
    return this.http.get<ShipmentMatchPage>(`${this.apiUrl}/order-releases/${releaseId}/matches`, { params });
  }

  previewReleaseAttachment(
    shipmentId: number,
    releaseIds: number[]
  ): Observable<ShipmentReleaseAttachmentPreview> {
    return this.http.post<ShipmentReleaseAttachmentPreview>(
      `${environment.restApiUrl}shipments/${shipmentId}/order-releases/preview`,
      { releaseIds }
    );
  }

  applyReleaseAttachment(
    shipmentId: number,
    attachmentId: number,
    request: ShipmentReleaseAttachmentApplyRequest = {}
  ): Observable<ShipmentReleaseAttachmentPreview> {
    return this.http.post<ShipmentReleaseAttachmentPreview>(
      `${environment.restApiUrl}shipments/${shipmentId}/order-releases/${attachmentId}/apply`,
      request
    );
  }

  previewDeliveryReleaseAmendment(
    shipmentId: number,
    deliveryId: number,
    releaseIds: number[]
  ): Observable<ShipmentDeliveryReleaseAmendment> {
    return this.http.post<ShipmentDeliveryReleaseAmendment>(
      `${environment.restApiUrl}shipments/${shipmentId}/deliveries/${deliveryId}/release-amendments/preview`,
      { releaseIds }
    );
  }

  applyDeliveryReleaseAmendment(
    shipmentId: number,
    deliveryId: number,
    amendmentId: number,
    request: ShipmentDeliveryReleaseAmendmentApplyRequest
  ): Observable<ShipmentDeliveryReleaseAmendment> {
    return this.http.post<ShipmentDeliveryReleaseAmendment>(
      `${environment.restApiUrl}shipments/${shipmentId}/deliveries/${deliveryId}/release-amendments/${amendmentId}/apply`,
      request
    );
  }

  private setParam(params: HttpParams, key: string,
    value: string | number | boolean | Date | null | undefined): HttpParams {
    if (value === null || value === undefined || value === '') {
      return params;
    }
    const serialized = value instanceof Date ? value.toISOString() : String(value);
    return params.set(key, serialized);
  }

  private setMultiParam(params: HttpParams, key: string,
    values: Array<string | number> | null | undefined): HttpParams {
    return (values || []).reduce((query, value) => query.append(key, String(value)), params);
  }

}
